/**
 * 生年月日から満年齢を求めるユーティリティ。
 *
 * 月だけを比較する実装（age === 16 && monthDiff < 0 のような判定）は、
 * 誕生月かつ誕生日前（例: 生年月日 8/20 / 今日 8/5）のケースを取りこぼし、
 * 実年齢が1歳下のユーザーを通してしまう。ここでは日まで比較する。
 *
 * また `new Date('YYYY-MM-DD')` は UTC 深夜として解釈されるため、
 * getMonth()/getDate() をローカル時刻で読むと環境によって1日ずれる。
 * 文字列を分解して数値比較することでタイムゾーンの影響を受けないようにする。
 */

/** 登録に必要な最低年齢 */
export const MIN_AGE = 16;

/**
 * 満年齢を返す。'YYYY-MM-DD' 形式以外・存在しない日付（2026-02-30 等）は null を返す。
 * @param birthDate 'YYYY-MM-DD' 形式の生年月日
 * @param today 基準日（省略時は現在日時。テスト用に注入できる）
 */
export const calcAge = (birthDate: string, today: Date = new Date()): number | null => {
  const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(birthDate.trim());
  if (!matched) return null;

  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);

  // 存在しない日付を弾く（Date に通すと 2026-02-30 → 3/2 のように繰り上がってしまう）
  const parsed = new Date(year, month - 1, day);
  if (
    parsed.getFullYear() !== year ||
    parsed.getMonth() !== month - 1 ||
    parsed.getDate() !== day
  ) {
    return null;
  }

  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() + 1 - month;
  // 誕生日がまだ来ていない年は1歳引く（同月なら日で判定）
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age -= 1;
  }
  return age;
};

/** 満 MIN_AGE 歳以上かどうか */
export const isOldEnough = (birthDate: string, today: Date = new Date()): boolean => {
  const age = calcAge(birthDate, today);
  return age !== null && age >= MIN_AGE;
};
